import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  MagnifyingGlassIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { Input } from '../../../../components/ui/input';
import { useDialogStore } from '../../../../store/dialogStore';
import {
  TournamentAdminService,
  isDuplicateRequest,
} from '../TournamentAdminService';
import {
  GAME_TYPES,
  GENDERS,
  SORTINGS,
  TournamentClub,
  TournamentFormData,
  TournamentRegistration,
  TournamentSavePayload,
} from '../TournamentAdmin.types';

type FormState = Omit<TournamentSavePayload, 'id'>;

const LIST_ROUTE = '/association/tournaments';

const SELECT_CLASS =
  'flex h-9 w-full rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm';

const emptyForm = (): FormState => ({
  title: '',
  organizerClubId: '',
  gameType: '',
  individualAllowed: false,
  sorting: '',
  startDate: '',
  endDate: '',
  price: '',
  registrationStart: '',
  registrationEnd: '',
  numberOfPlayers: '',
  numberOfSubstitutes: '',
  refereeEmail: '',
  gender: '',
  birthDateFrom: '',
  birthDateTo: '',
  maxHandicap: '',
  participantClubIds: [],
});

// "yyyy-mm-dd HH:MM:SS" from the API -> what <input type="datetime-local"> takes
const toDateTimeLocal = (value: string) => value.replace(' ', 'T');

const fromApi = (data: TournamentFormData): FormState => ({
  title: data.title ?? '',
  organizerClubId: data.organizerClubId ?? '',
  gameType: data.gameType ?? '',
  individualAllowed: Boolean(data.individualAllowed),
  sorting: data.sorting ?? '',
  startDate: data.startDate ?? '',
  endDate: data.endDate ?? '',
  price: data.price ?? '',
  registrationStart: toDateTimeLocal(data.registrationStart ?? ''),
  registrationEnd: toDateTimeLocal(data.registrationEnd ?? ''),
  numberOfPlayers:
    data.numberOfPlayers === null ? '' : String(data.numberOfPlayers),
  numberOfSubstitutes:
    data.numberOfSubstitutes === null ? '' : String(data.numberOfSubstitutes),
  refereeEmail: data.refereeEmail ?? '',
  gender: data.gender ?? '',
  birthDateFrom: data.birthDateFrom ?? '',
  birthDateTo: data.birthDateTo ?? '',
  maxHandicap: data.maxHandicap ?? '',
  participantClubIds: data.participantClubIds ?? [],
});

// "4 Spielern + 2 Ersatz" / "0 Spielern" for the warning and the hint next to a club
const squadSummary = (r: TournamentRegistration) =>
  r.substitutes > 0
    ? `${r.players} Spielern + ${r.substitutes} Ersatz`
    : `${r.players} Spielern`;

interface Props {
  /** The tournament to edit; undefined creates a new one. */
  tournamentId?: number;
}

const TournamentAdminForm: React.FC<Props> = ({ tournamentId }) => {
  const [form, setForm] = useState<FormState>(emptyForm());
  const [clubs, setClubs] = useState<TournamentClub[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [canDelete, setCanDelete] = useState(false);
  const [registrations, setRegistrations] = useState<
    Map<number, TournamentRegistration>
  >(new Map());
  const [clubSearch, setClubSearch] = useState('');

  const navigate = useNavigate();
  const openDialog = useDialogStore((state) => state.openDialog);
  const closeDialog = useDialogStore((state) => state.closeDialog);
  const setCloseButtonLabel = useDialogStore(
    (state) => state.setCloseButtonLabel,
  );
  const setSaveButtonLabel = useDialogStore(
    (state) => state.setSaveButtonLabel,
  );

  useEffect(() => {
    TournamentAdminService.fetchClubs()
      .then(setClubs)
      .catch((error) => {
        if (isDuplicateRequest(error)) return;
        console.error('Clubliste konnte nicht geladen werden:', error);
      });
  }, []);

  useEffect(() => {
    if (tournamentId === undefined) {
      setForm(emptyForm());
      setRegistrations(new Map());
      return;
    }
    const load = async () => {
      setIsLoading(true);
      try {
        const data = await TournamentAdminService.fetchTournament(tournamentId);
        setForm(fromApi(data));
        setCanDelete(Boolean(data.canDelete));
        setRegistrations(
          new Map(
            (data.registrations ?? [])
              .filter((r) => r.clubId !== null)
              .map((r) => [r.clubId as number, r]),
          ),
        );
        setIsLoading(false);
      } catch (error) {
        if (isDuplicateRequest(error)) return;
        console.error('Turnier konnte nicht geladen werden:', error);
        setIsLoading(false);
      }
    };
    load();
  }, [tournamentId]);

  const update = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleParticipant = (clubId: number) => {
    setForm((prev) => ({
      ...prev,
      participantClubIds: prev.participantClubIds.includes(clubId)
        ? prev.participantClubIds.filter((id) => id !== clubId)
        : [...prev.participantClubIds, clubId],
    }));
  };

  // Acts on what the search currently shows; a selected club hidden by the
  // search keeps its tick.
  const selectAllParticipants = (checked: boolean) => {
    const visibleIds = visibleClubs.map((club) => club.id);
    setForm((prev) => ({
      ...prev,
      participantClubIds: checked
        ? Array.from(new Set([...prev.participantClubIds, ...visibleIds]))
        : prev.participantClubIds.filter((id) => !visibleIds.includes(id)),
    }));
  };

  const visibleClubs = useMemo(() => {
    const term = clubSearch.trim().toLowerCase();
    if (!term) return clubs;
    return clubs.filter((club) =>
      `${club.name} ${club.clubNumberCountry}`.toLowerCase().includes(term),
    );
  }, [clubs, clubSearch]);

  const isSubmitDisabled = useMemo(() => {
    if (form.organizerClubId === '') return true;
    if (!form.startDate || !form.endDate) return true;
    if (!form.registrationStart || !form.registrationEnd) return true;
    return false;
  }, [form]);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const payload: TournamentSavePayload = {
      ...form,
      title: form.title.trim(),
      refereeEmail: form.refereeEmail.trim(),
      ...(tournamentId !== undefined ? { id: tournamentId } : {}),
    };

    try {
      setIsSaving(true);
      const result = await TournamentAdminService.save(payload);
      openDialog(
        <div className="grid gap-2 text-sm pt-6">
          <div className="font-bold">Eintrag gespeichert</div>
          <div>
            Das Turnier <b>#{result.id}</b> wurde erfolgreich{' '}
            {tournamentId === undefined ? 'angelegt' : 'bearbeitet'}.
          </div>
        </div>,
        closeDialog,
        true,
      );
      navigate(LIST_ROUTE);
    } catch (error) {
      // The axios interceptor already shows the server's message in a dialog.
      console.error('Turnier konnte nicht gespeichert werden:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const allSelected =
    visibleClubs.length > 0 &&
    visibleClubs.every((club) => form.participantClubIds.includes(club.id));

  const confirmDelete = () => {
    if (tournamentId === undefined) return;
    setCloseButtonLabel('Abbruch');
    setSaveButtonLabel('Löschen');
    openDialog(
      <div className="grid gap-3">
        <div className="text-base font-bold">
          Sind Sie sicher, dass Sie dieses Turnier löschen möchten?
        </div>
        <div className="text-xs text-gray-600">
          Dieser Vorgang kann nicht rückgängig gemacht werden. Die eingeladenen
          Clubs sehen das Turnier danach nicht mehr.
        </div>
        <div className="border rounded-md p-2 bg-gray-50">
          <div className="font-medium">#{tournamentId}</div>
          <div className="font-medium">{form.title}</div>
        </div>
      </div>,
      async () => {
        try {
          await TournamentAdminService.delete(tournamentId);
          closeDialog();
          setSaveButtonLabel('Weiter');
          navigate(LIST_ROUTE);
        } catch (error) {
          // The axios interceptor already shows the server's message in a dialog.
          console.error('Turnier konnte nicht gelöscht werden:', error);
        }
      },
    );
  };

  return (
    <div className="bg-white border rounded-md p-4 mx-5">
      <form className="grid gap-5" onSubmit={handleSubmit}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <button
              type="button"
              onClick={() => navigate(LIST_ROUTE)}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-[#52A445] mb-1"
            >
              <ArrowLeftIcon className="w-3 h-3" />
              Zurück zur Übersicht
            </button>
            <div className="font-medium text-lg">
              {tournamentId === undefined
                ? 'Neues Turnier anlegen'
                : `Bestehendes Turnier bearbeiten (#${tournamentId})`}
            </div>
          </div>
          {form.title.length > 0 && tournamentId === undefined && (
            <div
              className="border border-red-200 bg-red-50 py-1 hover:border-red-500 cursor-pointer px-1.5 rounded-md flex items-center gap-1 justify-center self-start"
              onClick={() => setForm(emptyForm())}
            >
              <TrashIcon className="w-4 h-4 text-red-500" />
              <span className="text-[11px] text-red-500">
                Formular zurücksetzen
              </span>
            </div>
          )}
        </div>

        {isLoading && (
          <div className="text-xs text-gray-500">Turnier wird geladen ...</div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-5">
          <div className="grid gap-5 content-start">
            <div className="grid gap-2 min-w-0">
              <label className="text-sm text-gray-600">Bezeichnung</label>
              <textarea
                className="flex w-full rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm font-bold focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                rows={3}
                value={form.title}
                onChange={(e) => update('title', e.target.value)}
                placeholder="Bezeichnung des Turniers"
              />
            </div>

            <div className="grid gap-2 min-w-0">
              <label className="text-sm text-gray-600">Veranstaltungsort</label>
              <select
                className={SELECT_CLASS}
                value={form.organizerClubId}
                onChange={(e) =>
                  update(
                    'organizerClubId',
                    e.target.value === '' ? '' : Number(e.target.value),
                  )
                }
              >
                <option value="">- bitte auswählen -</option>
                {clubs.map((club) => (
                  <option key={club.id} value={club.id}>
                    {club.name} ({club.clubNumberCountry})
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-2 min-w-0">
                <label className="text-sm text-gray-600">Wettspielart</label>
                <select
                  className={SELECT_CLASS}
                  value={form.gameType}
                  onChange={(e) => update('gameType', e.target.value)}
                >
                  <option value="">- bitte auswählen -</option>
                  {GAME_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {type}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid gap-2 min-w-0">
                <label className="text-sm text-gray-600">Sortierung</label>
                <select
                  className={SELECT_CLASS}
                  value={form.sorting}
                  onChange={(e) => update('sorting', e.target.value)}
                >
                  <option value="">- bitte auswählen -</option>
                  {SORTINGS.map((sorting) => (
                    <option key={sorting.value} value={sorting.value}>
                      {sorting.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid gap-2 min-w-0">
              <label className="text-sm text-gray-600">
                zusätzl. Einzelmeldung
              </label>
              <div className="flex items-center gap-4 text-sm">
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="radio"
                    name="individualAllowed"
                    checked={form.individualAllowed}
                    onChange={() => update('individualAllowed', true)}
                  />
                  Ja
                </label>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="radio"
                    name="individualAllowed"
                    checked={!form.individualAllowed}
                    onChange={() => update('individualAllowed', false)}
                  />
                  Nein
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="grid gap-2 min-w-0">
                <label className="text-sm text-gray-600">Turnierdatum ab</label>
                <Input
                  type="date"
                  className="font-bold text-sm min-w-0 w-full"
                  value={form.startDate}
                  onChange={(e) => update('startDate', e.target.value)}
                />
              </div>
              <div className="grid gap-2 min-w-0">
                <label className="text-sm text-gray-600">
                  Turnierdatum bis
                </label>
                <Input
                  type="date"
                  className="font-bold text-sm min-w-0 w-full"
                  value={form.endDate}
                  onChange={(e) => update('endDate', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
              <div className="grid gap-2 min-w-0">
                <label className="text-sm text-gray-600">Meldung ab</label>
                <Input
                  type="datetime-local"
                  step={1}
                  className="font-bold text-sm min-w-0 w-full"
                  value={form.registrationStart}
                  onChange={(e) => update('registrationStart', e.target.value)}
                />
              </div>
              <div className="grid gap-2 min-w-0">
                <label className="text-sm text-gray-600">Meldung bis</label>
                <Input
                  type="datetime-local"
                  step={1}
                  className="font-bold text-sm min-w-0 w-full"
                  value={form.registrationEnd}
                  onChange={(e) => update('registrationEnd', e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="grid gap-2 min-w-0">
                <label className="text-sm text-gray-600">
                  Meldegebühr (EUR)
                </label>
                <Input
                  inputMode="decimal"
                  value={form.price}
                  onChange={(e) =>
                    update('price', e.target.value.replace(/[^0-9.,]/g, ''))
                  }
                  placeholder="0,00"
                />
              </div>
              <div className="grid gap-2 min-w-0">
                <label className="text-sm text-gray-600">Anzahl Spieler</label>
                <Input
                  inputMode="numeric"
                  value={form.numberOfPlayers}
                  onChange={(e) =>
                    update(
                      'numberOfPlayers',
                      e.target.value.replace(/[^0-9]/g, ''),
                    )
                  }
                />
              </div>
              <div className="grid gap-2 min-w-0">
                <label className="text-sm text-gray-600">
                  Anzahl Ersatzspieler
                </label>
                <Input
                  inputMode="numeric"
                  value={form.numberOfSubstitutes}
                  onChange={(e) =>
                    update(
                      'numberOfSubstitutes',
                      e.target.value.replace(/[^0-9]/g, ''),
                    )
                  }
                />
              </div>
            </div>

            <div className="grid gap-2 min-w-0">
              <label className="text-sm text-gray-600">
                E-Mail Spielleitung
              </label>
              <Input
                type="email"
                value={form.refereeEmail}
                onChange={(e) => update('refereeEmail', e.target.value)}
                placeholder="spielleitung@verband.de"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="grid gap-2 min-w-0">
                <label className="text-sm text-gray-600">Geschlecht</label>
                <select
                  className={SELECT_CLASS}
                  value={form.gender}
                  onChange={(e) => update('gender', e.target.value)}
                >
                  <option value="">- beide erlaubt -</option>
                  {GENDERS.map((gender) => (
                    <option key={gender.value} value={gender.value}>
                      {gender.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="grid gap-2 min-w-0">
                <label className="text-sm text-gray-600">Geburtsdatum ab</label>
                <Input
                  type="date"
                  className="text-sm min-w-0 w-full"
                  value={form.birthDateFrom}
                  onChange={(e) => update('birthDateFrom', e.target.value)}
                />
              </div>
              <div className="grid gap-2 min-w-0">
                <label className="text-sm text-gray-600">
                  Geburtsdatum bis
                </label>
                <Input
                  type="date"
                  className="text-sm min-w-0 w-full"
                  value={form.birthDateTo}
                  onChange={(e) => update('birthDateTo', e.target.value)}
                />
              </div>
            </div>

            <div className="grid gap-2 min-w-0">
              <label className="text-sm text-gray-600">
                Maximales HCP pro Spieler
              </label>
              <Input
                inputMode="decimal"
                value={form.maxHandicap}
                onChange={(e) =>
                  update('maxHandicap', e.target.value.replace(/[^0-9.,]/g, ''))
                }
                placeholder="z. B. 36,0"
              />
            </div>
          </div>

          <div className="grid gap-2 content-start">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">Teilnehmer</div>
              <label className="flex items-center gap-1 text-xs text-gray-600 cursor-pointer">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={(e) => selectAllParticipants(e.target.checked)}
                />
                {clubSearch.trim() ? 'gefilterte auswählen' : 'alle auswählen'}{' '}
                ({form.participantClubIds.length} / {clubs.length})
              </label>
            </div>

            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                className="pl-10"
                placeholder="Clubname oder -nummer suchen ..."
                value={clubSearch}
                onChange={(e) => setClubSearch(e.target.value)}
              />
            </div>

            <div className="grid gap-1 border rounded-md p-2 bg-gray-50 text-sm">
              {visibleClubs.map((club) => {
                const registration = registrations.get(club.id);
                const ticked = form.participantClubIds.includes(club.id);
                return (
                  <div key={club.id}>
                    <label className="flex items-center gap-2 cursor-pointer hover:bg-white rounded px-1 py-0.5">
                      <input
                        type="checkbox"
                        checked={ticked}
                        onChange={() => toggleParticipant(club.id)}
                      />
                      <span className="flex-1">{club.name}</span>
                      <span className="text-xs text-gray-500">
                        ({club.clubNumberCountry})
                      </span>
                    </label>
                    {registration && ticked && (
                      <div className="ml-7 mb-1 text-xs text-green-700">
                        Meldung liegt vor: {squadSummary(registration)}
                      </div>
                    )}
                    {registration && !ticked && (
                      <div className="ml-7 mb-1 text-xs text-red-600 font-medium">
                        Achtung: Dieser Club hat für dieses Turnier bereits eine
                        Meldung mit {squadSummary(registration)} abgegeben. Ohne
                        Häkchen sieht er das Turnier nicht mehr und kann seine
                        Meldung nicht mehr bearbeiten; die bereits gemeldeten
                        Spieler bleiben gespeichert.
                      </div>
                    )}
                  </div>
                );
              })}
              {visibleClubs.length === 0 && (
                <div className="text-xs text-gray-500">
                  {clubSearch.trim()
                    ? `Kein Club passt zu "${clubSearch.trim()}".`
                    : 'Keine Clubs gefunden.'}
                </div>
              )}
            </div>
          </div>
        </div>

        <div
          className="flex sticky bottom-0 border-t bg-white p-3 items-center gap-3"
          style={{ boxShadow: '0 -13px 14px 0 rgba(0, 0, 0, 0.05)' }}
        >
          <div className="flex-1 text-xs">
            {tournamentId !== undefined && !isLoading && canDelete && (
              <button
                type="button"
                onClick={confirmDelete}
                className="flex items-center gap-1 text-red-600 hover:underline font-medium"
              >
                <TrashIcon className="w-4 h-4" />
                Turnier löschen
              </button>
            )}
            {tournamentId !== undefined && !isLoading && !canDelete && (
              <span className="text-gray-500">
                Löschen nicht möglich: Es liegen bereits Meldungen von Clubs
                vor.
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => navigate(LIST_ROUTE)}
            className="px-3 py-2 rounded-md text-sm border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={isSubmitDisabled || isSaving || isLoading}
            className={`px-3 py-2 rounded-md text-sm text-white ${
              isSubmitDisabled
                ? 'bg-gray-300 cursor-not-allowed'
                : 'bg-[#52A445] hover:bg-green-700'
            }`}
          >
            {isSaving ? 'Speichern ...' : 'Eintrag speichern'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TournamentAdminForm;
